import { useContext, useMemo } from 'react';

import { Checkbox } from '@monoimin/atoms';

import { useParamsUpdater } from '@monoimin/hooks';

import { FiltersContext } from './FiltersProvider';

import type {
  FilterStateValue,
  OptionFilter as OptionFilterType,
} from '../../types';
import type { EnOrRuLocale } from '@monoimin/sb-types';

type OptionFilterProps = OptionFilterType & { locale: EnOrRuLocale };

const findMatchWithFiltersState = (
  filtersState: FilterStateValue,
  value: FilterStateValue
) => {
  if (Array.isArray(filtersState) && typeof value === 'string') {
    return Boolean(
      filtersState.find((stateItem) => {
        return value === stateItem;
      })
    );
  }

  return false;
};

export const OptionFilter = ({
  title,
  filterConfigs,
  dependencies,
  locale,
}: OptionFilterProps) => {
  // На данный момент обрабатываем только одно значение
  const firstValue = filterConfigs[0];

  const { onChange: onParamsChange } = useParamsUpdater();

  const [filtersState] = useContext(FiltersContext);

  const current = filtersState?.[firstValue.filterKey] || [];

  const isSelected = Array.isArray(current)
    ? Boolean(current.find((item) => item === firstValue.value))
    : false;

  const allowRendering = useMemo(() => {
    if (Array.isArray(dependencies) && dependencies.length > 0) {
      return dependencies.every((item) => {
        const { filterKey, value } = item;

        const currentState = filtersState?.[filterKey];

        if (!currentState) {
          return false;
        }

        return findMatchWithFiltersState(currentState, value);
      });
    }

    return true;
  }, [dependencies, filtersState]);

  const onOptionClick = () => {
    if (!Array.isArray(current)) {
      return;
    }

    if (isSelected) {
      const filtered = current.filter((item) => item !== firstValue.value);

      onParamsChange({
        [firstValue.filterKey]: filtered,
      });
    } else {
      onParamsChange({
        [firstValue.filterKey]: [...current, firstValue.value],
      });
    }
  };

  if (allowRendering || isSelected) {
    return (
      <button className='flex items-center gap-2' onClick={onOptionClick}>
        <Checkbox
          checked={isSelected}
          readOnly
          className='pointer-events-none'
        />
        <span className='whitespace-nowrap'>{title[locale]}</span>
      </button>
    );
  }

  return null;
};
